import React, { useCallback, useContext, useEffect, useState } from 'react';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import styled from 'styled-components';

import Loading from 'components/Loading';
import Row from 'components/Common/Row';
import EntityDefinitionsContext from 'contexts/EntityDefinitionsContext';
import usePopup from 'hooks/usePopup';
import TextInput from 'kingpin/atoms/TextInput';
import Typography from 'kingpin/atoms/Typography';
import Button from 'kingpin/atoms/Button';

import AddMappedUserForm from './AddMappedUserForm';
import PortalUserGridRow from './PortalUserGridRow';
import MappedUserItem from './MappedUserItem';
import _ from 'lodash';
import useMappedUsers from '../../V3/Workspace/screens/UsersScreen/ProductAppUsersScreen/useMappedUsers';

const ListWrapper = styled.div`
  flex: 1;
  padding: 0px 24px 16px 24px;
  overflow-y: auto;
`;

const List = styled.div`
  max-height: 100%;
  overflow-y: scroll;
  padding: 0px;
  padding-bottom: 64px;
`;

const useUsers = (entityId: string, entityAppId: string) => {
  const { mappedUsers, setMappedUsers, isLoading } = useMappedUsers({
    entityId,
    entityAppId,
  });
  const [users, setUsers] = useState<MappedUser[]>(mappedUsers);
  const [searchText, setSearchText] = useState<string>('');
  const onSearchChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(event.target.value);
    },
    [],
  );

  useEffect(() => {
    const baseUsers = mappedUsers.filter((u) =>
      (u.user.displayName ?? '')
        .toLowerCase()
        .includes(searchText.toLowerCase()),
    );
    const sortedUsers = _.sortBy(baseUsers, 'user.displayName');
    setUsers(sortedUsers);
  }, [mappedUsers, searchText]);

  return {
    users,
    searchText,
    onSearchChanged,
    setMappedUsers,
    isLoading,
  };
};
const PortalMappedUsers = ({
  entity,
  entityAppId,
  entityPortalName,
}: {
  entity: EntityDetails.Entity;
  entityAppId: string;
  entityPortalName: string;
}) => {
  const { isOpen, open, close } = usePopup();
  const { users, searchText, onSearchChanged, isLoading, setMappedUsers } =
    useUsers(entity.id, entityAppId);

  return (
    <>
      <div
        style={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Row spaceBetween centerAlign style={{ paddingRight: 32 }}>
          <div style={{ width: 400, padding: '16px 24px' }}>
            <TextInput
              value={searchText}
              onChange={onSearchChanged}
              icon="search"
              inputSize="Small"
              placeholder="Search"
              autoFocus
            />
          </div>
          <Button
            size={'Small'}
            type={'Secondary'}
            label={`Add ${entityPortalName}`}
            icon="add"
            onClick={open}
          />
        </Row>
        <PortalUserGridRow
          isHeader
          NameCol={<Typography.Body type="Label">Name</Typography.Body>}
          EmailCol={<Typography.Body type="Label">Email</Typography.Body>}
          FilterValueCol={
            <Typography.Body type="Label">{entity.name}</Typography.Body>
          }
        />
        <ListWrapper>
          {isLoading && <Loading />}
          {!isLoading && (
            <List>
              {users.map((u) => (
                <MappedUserItem
                  user={u}
                  entity={entity}
                  entityPortalName={entityPortalName}
                  setMappedUsers={setMappedUsers}
                  key={u.user.id}
                  mappedUsers={users}
                />
              ))}
            </List>
          )}
        </ListWrapper>
      </div>
      <ModalTransition>
        {isOpen && (
          <Modal
            onClose={close}
            shouldScrollInViewport={false}
            autoFocus={false}
          >
            <AddMappedUserForm
              close={close}
              entity={entity}
              setMappedUsers={setMappedUsers}
              entityPortalName={entityPortalName}
              mappedUsers={users}
              entityAppId={entityAppId}
            />
          </Modal>
        )}
      </ModalTransition>
    </>
  );
};

export const getMappedEntityLinkId = (
  portal: EngagementPortal,
  entityId: string,
  entityAppId?: string,
) => {
  return portal.entityLinks.find((el) => {
    if (entityAppId === undefined) {
      return el.entityId === entityId;
    }
    return el.entityId === entityId && el.entityAppId === entityAppId;
  });
};

const Gate = ({
  portal,
  entityId,
  entityAppId,
}: {
  portal: EngagementPortal;
  entityId: string;
  entityAppId: string;
}) => {
  const [entity, setEntity] = useState<EntityDetails.Entity | undefined>();
  const [entityPortalName, setEntityPortalName] = useState<
    string | undefined
  >();
  const { entityDefinitions } = useContext(EntityDefinitionsContext);
  useEffect(() => {
    const mappedEntityLink = getMappedEntityLinkId(
      portal,
      entityId,
      entityAppId,
    );

    const entityName = (() => {
      if (!mappedEntityLink) {
        return undefined;
      }

      return mappedEntityLink.usersTabLabel;
    })();
    setEntityPortalName(entityName);
    setEntity(entityDefinitions.find((e) => e.id === entityId));
  }, [entityAppId, entityDefinitions, entityId, portal, portal.entityLinks]);

  if (!entity || !entityPortalName) {
    return null;
  }

  return (
    <PortalMappedUsers
      entity={entity}
      entityPortalName={entityPortalName}
      entityAppId={entityAppId}
    />
  );
};

export default Gate;
